import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Download ChessMate from the App Store or visit our web app. Tap "Sign Up" and enter your email address and a display name. You will receive a verification email — click the link to activate your account. Once verified, you can sign in and start playing immediately.',
  },
  {
    question: 'How do I start a game?',
    answer:
      'From the home screen, tap "Play". You can choose Quick Match (automatically pairs you with a similar-rated opponent), Play a Friend (send a game invite), Play vs AI (choose a bot from 400 to 2500 Elo), or Custom Game (select time control, rated or casual, and game variant).',
  },
  {
    question: 'What game variants are available?',
    answer:
      'ChessMate supports Standard chess, Chess960 (Fischer Random), King of the Hill, and 3-Check.',
  },
  {
    question: 'What time controls are available?',
    answer:
      'Bullet (1 minute), Blitz (3 minutes + 2 second increment, or 5 minutes), Rapid (10 minutes, or 15 minutes + 10 second increment), and Classical (30 minutes and above).',
  },
  {
    question: 'How does matchmaking work?',
    answer:
      'When you tap "Quick Match", ChessMate pairs you with an opponent whose Elo rating is close to yours in the selected time control. The system searches for the closest match available and widens the search range gradually if no exact match is found. Rated games affect your rating; casual games do not.',
  },
  {
    question: 'How are ratings calculated?',
    answer:
      'ChessMate uses the Elo rating system. Your rating goes up when you win and down when you lose, with the amount depending on the rating difference between you and your opponent. Each time control (bullet, blitz, rapid, classical) has a separate rating. New accounts start at 1200 Elo.',
  },
  {
    question: 'Can I review my games after they finish?',
    answer:
      'Yes. Go to your profile and tap "Game History" to see all your past games. Tap any game to review it move by move. Platinum and Diamond subscribers have access to full computer analysis powered by Stockfish.',
  },
  {
    question: 'What subscription tiers are available?',
    answer:
      'ChessMate offers three premium tiers: Gold (£2.99/mo or £29/yr — ad-free, unlimited puzzles, custom themes, puzzle rush), Platinum (£4.99/mo or £49/yr — adds full game analysis, lessons library, advanced stats, tournament creation), and Diamond (£9.99/mo or £99/yr — adds AI coaching, opening preparation tools, priority matchmaking).',
  },
  {
    question: 'How do I subscribe?',
    answer:
      'On iOS, subscriptions are managed through Apple In-App Purchase. On the web, subscriptions are processed through Stripe. Go to Settings > Subscription and choose your preferred tier and billing cycle.',
  },
  {
    question: 'How do I manage or cancel my subscription?',
    answer:
      'On iOS, go to your device Settings > Apple ID > Subscriptions > ChessMate to change your plan or cancel. On the web, go to Settings > Subscription within ChessMate. Cancellation takes effect at the end of your current billing period — you retain access to premium features until that date.',
  },
  {
    question: 'Will I lose my data if I cancel?',
    answer:
      'No. Your game history, ratings, puzzle progress, and account data are all retained when you cancel. You will simply revert to the free tier features.',
  },
  {
    question: 'Can I get a refund?',
    answer:
      'For iOS purchases, refunds are handled by Apple — visit reportaproblem.apple.com to request one. For web purchases, contact us at support@wolftechnologies.co.uk within 14 days of purchase for a refund.',
  },
  {
    question: 'The app is not loading or crashes on startup',
    answer:
      'Try closing the app completely and reopening it. Check that you have a stable internet connection and are running the latest version (check the App Store for updates). Restart your device. If the issue persists, uninstall and reinstall the app — your data is saved to your account, not your device.',
  },
  {
    question: 'I am experiencing connection problems during a game',
    answer:
      'ChessMate requires a stable internet connection for live multiplayer games. Check your Wi-Fi or mobile data connection, move closer to your router if on Wi-Fi, and avoid switching between Wi-Fi and mobile data mid-game. If you disconnect during a rated game, you have a short window to reconnect before the game is forfeited.',
  },
  {
    question: 'My rating seems incorrect',
    answer:
      'Ratings are updated after each rated game. Check your recent game history to confirm all results are recorded correctly. If you still believe there is a discrepancy, contact us with the details.',
  },
  {
    question: 'I cannot log in to my account',
    answer:
      'Make sure you are using the correct email address. Try resetting your password using the "Forgot Password" link on the sign-in screen. Check your email (including spam/junk folders) for the password reset link. If you still cannot access your account, contact us for assistance.',
  },
  {
    question: 'Push notifications are not working',
    answer:
      'Make sure notifications are enabled for ChessMate in your device settings (Settings > Notifications > ChessMate). Also check that "Do Not Disturb" mode is turned off.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const ChessMateSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">ChessMate Support</h1>
          <p className="legal-page__subtitle">
            Get help with ChessMate by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all support requests within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">ChessMate</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/chessmate/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ChessMateSupport;
